import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Category_ } from 'src/api/db/apiCategory'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shared/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import {
  RadioGroup,
  RadioGroupItem,
} from 'src/shared/components/ui/radio-group'
import { Label } from 'src/shared/components/ui/label'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'

export type MoveMode = 'category' | 'products'

interface MoveProductsDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (targetCategoryId: number, mode: MoveMode) => Promise<void>
  sourceCategory: Category_ | null
  productCount: number
  isLoading?: boolean
  allCategories: Category_[]
  targetCategoryId?: number | null
}

export const MoveProductsDialog = ({
  isOpen,
  onClose,
  onConfirm,
  sourceCategory,
  productCount,
  isLoading = false,
  allCategories,
  targetCategoryId,
}: MoveProductsDialogProps) => {
  const { t } = useLocalization()
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  )
  const [moveMode, setMoveMode] = useState<MoveMode>('products')
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Pre-select target category when opened from drag-and-drop
  useEffect(() => {
    if (isOpen && targetCategoryId) {
      setSelectedCategoryId(targetCategoryId)
    } else if (!isOpen) {
      // Reset when dialog closes
      setSelectedCategoryId(null)
      setMoveMode('products')
    }
  }, [isOpen, targetCategoryId])

  // Build category map for quick lookup
  const categoryMap = useMemo(() => {
    const map = new Map<number, Category_>()
    allCategories.forEach((cat) => {
      map.set(cat.id, cat)
    })
    return map
  }, [allCategories])

  // Helper function to get translated category title
  const getTranslatedTitle = useCallback(
    (cat: Category_) => {
      const parts = cat.title?.split(' || ') || []
      return t(
        parts[0] || 'Category',
        parts[1] || cat.title || `Category ${cat.id}`
      )
    },
    [t]
  )

  // Function to get the full hierarchy path for a category (with translations)
  const getCategoryPath = useCallback(
    (category: Category_): string => {
      const path: string[] = []
      let currentCategory: Category_ | undefined = category
      const visited = new Set<number>() // Prevent infinite loops

      while (currentCategory && !visited.has(currentCategory.id)) {
        visited.add(currentCategory.id)
        path.unshift(getTranslatedTitle(currentCategory))

        if (currentCategory.parent_category_id) {
          currentCategory = categoryMap.get(currentCategory.parent_category_id)
        } else {
          break
        }
      }

      return path.join(' > ')
    },
    [categoryMap, getTranslatedTitle]
  )

  // Helper function to check if a category is a descendant of another category
  const isDescendant = useCallback(
    (categoryId: number, ancestorId: number): boolean => {
      const visited = new Set<number>()
      let currentCategory = categoryMap.get(categoryId)

      while (currentCategory && !visited.has(currentCategory.id)) {
        visited.add(currentCategory.id)
        if (currentCategory.id === ancestorId) {
          return true
        }
        if (currentCategory.parent_category_id) {
          currentCategory = categoryMap.get(currentCategory.parent_category_id)
        } else {
          break
        }
      }
      return false
    },
    [categoryMap]
  )

  // Filter out the source category and its descendants from the list
  // (to prevent circular references when moving category)
  const availableCategories = useMemo(() => {
    if (!sourceCategory) return allCategories

    return allCategories.filter((cat) => {
      // Can't select the source category itself
      if (cat.id === sourceCategory.id) return false

      // If moving category, filter out descendants to prevent circular references
      // (can't move a category into one of its own descendants)
      if (moveMode === 'category') {
        return !isDescendant(cat.id, sourceCategory.id)
      }

      return true
    })
  }, [allCategories, sourceCategory, moveMode, isDescendant])

  const selectedCategory = useMemo(() => {
    return availableCategories.find((cat) => cat.id === selectedCategoryId)
  }, [availableCategories, selectedCategoryId])

  // Get translated source category title
  const sourceCategoryTitle = useMemo(() => {
    return sourceCategory ? getTranslatedTitle(sourceCategory) : ''
  }, [sourceCategory, getTranslatedTitle])

  const handleConfirm = async () => {
    if (selectedCategoryId) {
      await onConfirm(selectedCategoryId, moveMode)
      setSelectedCategoryId(null)
      setMoveMode('products') // Reset to default
    }
  }

  const handleClose = () => {
    setSelectedCategoryId(null)
    setMoveMode('products') // Reset to default
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {moveMode === 'category'
              ? t('Move Category', 'Flytta kategori')
              : t('Move Products', 'Flytta produkter')}
          </DialogTitle>
          <DialogDescription>
            {sourceCategory && (
              <>
                {moveMode === 'category'
                  ? t(
                      `Move category "${sourceCategoryTitle}" as a subcategory under another category`,
                      `Flytta kategorin "${sourceCategoryTitle}" som en underkategori under en annan kategori`
                    )
                  : t(
                      `Move ${productCount} product(s) from "${sourceCategoryTitle}" to another category`,
                      `Flytta ${productCount} produkt(er) från "${sourceCategoryTitle}" till en annan kategori`
                    )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-3">
            <Label className="text-base font-medium">
              {t('Move Options', 'Flyttalternativ')}
            </Label>
            <RadioGroup
              value={moveMode}
              onValueChange={(value) => setMoveMode(value as MoveMode)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="category" id="move-category" />
                <Label
                  htmlFor="move-category"
                  className="font-normal cursor-pointer"
                >
                  {t(
                    'Move category (makes it a subcategory)',
                    'Flytta kategori (gör den till en underkategori)'
                  )}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="products" id="move-products" />
                <Label
                  htmlFor="move-products"
                  className="font-normal cursor-pointer"
                >
                  {t(
                    `Move only products (${productCount} product(s))`,
                    `Flytta endast produkter (${productCount} produkt(er))`
                  )}
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>{t('Target Category', 'Målkategori')}</Label>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  role="combobox"
                  aria-expanded={isPopoverOpen}
                  className="w-full justify-between"
                >
                  {selectedCategory
                    ? getCategoryPath(selectedCategory)
                    : t('Select target category...', 'Välj målkategori...')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t('Search categories...', 'Sök kategorier...')}
                    className="h-9"
                  />
                  <CommandList>
                    <CommandEmpty>
                      {t('No category found.', 'Ingen kategori hittades.')}
                    </CommandEmpty>
                    <CommandGroup>
                      {availableCategories.map((cat) => {
                        const categoryPath = getCategoryPath(cat)
                        return (
                          <CommandItem
                            key={cat.id}
                            value={categoryPath}
                            onSelect={() => {
                              setSelectedCategoryId(cat.id)
                              setIsPopoverOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedCategoryId === cat.id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            />
                            {categoryPath}
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleClose} disabled={isLoading}>
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selectedCategoryId || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('Moving...', 'Flyttar...')}
              </>
            ) : moveMode === 'category' ? (
              t('Move Category', 'Flytta kategori')
            ) : (
              t('Move Products', 'Flytta produkter')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
